import React from "react";
import styled from "styled-components";
import { TextField } from "@material-ui/core";
import { withFormik, FormikValues } from "formik";
import CallToActionButton from "components/shared/buttons/CallToActionButton";
import { isNil, get } from "lodash";
import { object, string } from "yup";
import ErrorMessage from "components/shared/alerts/ErrorMessage";
import { ApolloConsumer } from "react-apollo";
import gql from "graphql-tag";
import { withRouter } from "next/router";
import { useAutoFocusOnDesktop } from "hooks/ui/useAutoFocusOnDesktop";

const signupSubscriber = gql`
  mutation signupSubscriber($email: String!, $signupReferralCode: ID!) {
    signupSubscriber(email: $email, signupReferralCode: $signupReferralCode) {
      id
    }
  }
`;

const Title = styled.h1`
  font-family: ${(props) => props.theme.fonts.poppins};
`;

const Text = styled.p`
  font-family: ${(props) => props.theme.fonts.poppins};
`;

const LogoSection = styled.div`
  max-width: 400px;
`;

const FormSection = styled.div`
  max-width: 600px;
`;

const Logo = styled.img.attrs({
  src: "/static/lettermark_logo.png",
})`
  height: 100px;
  width: 100px;
`;

const SignupButton = styled<any>(CallToActionButton)`
  @media (max-width: ${(props) => props.theme.breakpoints.lg}) {
    width: ${(props) => (props.loggedIn ? "100%" : "auto")};
  }
`;

interface FormProps {
  loggedIn: boolean;
  email: string;
  router: any;
  signupReferralCode: string;
  apolloClient: any;
  afterSignup: (email: string, subscriberId: string) => void;
}

const NewsletterSignupForm = withRouter(
  withFormik<FormProps, FormikValues>({
    mapPropsToValues: (props) => ({
      email: props.email,
      submission: "",
    }),

    validationSchema: object().shape({
      email: string()
        .required("You forgot to enter your email.")
        .email("You did not give a valid email address"),
    }),

    handleSubmit: (values, { props, setSubmitting, setErrors }) => {
      setSubmitting(true);
      const signupReferralCode = get(props.router, "query.r", "");
      props.apolloClient
        .mutate({
          mutation: signupSubscriber,
          variables: { email: values.email, signupReferralCode },
          refetchQueries: ["newsletterPageQuery"],
          awaitRefetchQueries: true,
        })
        .then((result) => {
          props.afterSignup(values?.email, result?.data?.signupSubscriber?.id);
          setSubmitting(false);
        })
        .catch((e) => {
          setSubmitting(false);
          setErrors({
            submission: "Something went wrong. Please try again.",
          });
        });
    },
  })(
    ({
      loggedIn,
      router,
      errors,
      isSubmitting,
      handleSubmit,
      setErrors,
      values,
      setValues,
      submitCount,
    }) => {
      const hasSubmitted = submitCount > 0;
      const hasEmailError = !isNil(errors.email) && hasSubmitted;
      const hasSubmissionError = !isNil(errors.submission) && hasSubmitted;
      const hasError = hasEmailError || hasSubmissionError;
      const errorMessage: string = hasEmailError
        ? String(errors.email)
        : hasSubmissionError
        ? String(errors.submission)
        : "Something went wrong. Please try again.";
      const { autoFocusInputRef } = useAutoFocusOnDesktop();

      return (
        <div className="flex flex-grow lg:flex-row flex-col items-center justify-center w-full h-full p-4">
          <ErrorMessage
            show={hasError}
            text={errorMessage}
            onClickClose={() => setErrors({})}
          />
          <LogoSection className="flex items-center justify-start lg:flex-grow flex-col p-4 mb-10">
            <div style={{ width: "100px" }}>
              <Logo className="mb-4" />
              <Text className="text-xl text-left font-bold leading-tight">
                The <br /> Juggernaut <br /> Weekly <br />
              </Text>
            </div>
          </LogoSection>
          <FormSection className="flex lg:flex-grow flex-col justify-center">
            <Title className="text-xl font-bold mb-3">
              A <span className="underline">free</span> Sunday newsletter that
              curates the best stories and global news on South Asia(ns)
            </Title>

            <Text className="text-xl mb-6">
              Join over 34,000 readers and get smarter about 25% of the world in
              less than 8 minutes.
            </Text>
            <div className="flex lg:flex-row flex-col mb-4">
              {!loggedIn && (
                <TextField
                  className="flex flex-grow text-lg lg:mr-2 lg:mb-0 mb-2"
                  label="Email"
                  variant="outlined"
                  disabled={isSubmitting}
                  error={hasError}
                  onChange={(e) =>
                    setValues({ ...values, email: e.target.value })
                  }
                  inputRef={autoFocusInputRef}
                />
              )}
              <SignupButton
                loggedIn={loggedIn}
                onClick={handleSubmit}
                disabled={isSubmitting}
                className="text-md w-48"
              >
                {isSubmitting ? "Signing Up..." : "Sign Up"}
              </SignupButton>
            </div>

            <Text className="text-md italic font-bold mb-3">
              “The Juggernaut has quickly become my go-to source for South Asian
              news and stories.” — Brian Levin, former Huffington Post senior
              editor
            </Text>
          </FormSection>
        </div>
      );
    }
  )
);

export default (props) => (
  <ApolloConsumer>
    {(apolloClient) => (
      <NewsletterSignupForm {...props} apolloClient={apolloClient} />
    )}
  </ApolloConsumer>
);
